import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/api";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import careersData from "../../data/careers.json";
import {
  MapPin,
  Clock,
  Briefcase,
  Calendar,
  ChevronDown,
  ChevronRight,
  Mail,
  ExternalLink,
  AlertCircle,
} from "lucide-react";

const JobCard = ({ job, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { y: 60, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.8,
          delay: (index % 5) * 0.1, // Staggers newly revealed paginated batches smoothly
          ease: "back.out(1.2)",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 85%",
          },
        },
      );
    }, cardRef);

    return () => ctx.revert();
  }, [index]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);

    if (!isExpanded) {
      gsap.fromTo(
        contentRef.current,
        { height: 0, opacity: 0 },
        { height: "auto", opacity: 1, duration: 0.5, ease: "power3.out" },
      );
    } else {
      gsap.to(contentRef.current, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: "power3.in",
      });
    }
  };

  const handleApply = () => {
    if (job.apply_link) {
      window.open(job.apply_link, "_blank", "noopener,noreferrer");
    } else {
      const subject = `Application for ${job.job_title} Position`;
      const body = `Dear Agora Consulting Team,\n\nI am writing to express my interest in the ${job.job_title} position.\n\nBest regards,\n[Your Name]`;
      window.location.href = `mailto:${careersData?.application?.contact?.email || "hr@agora.com"}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  return (
    <div
      ref={cardRef}
      className="group relative bg-white rounded-3xl border border-slate-200 hover:border-primary-300 transition-all duration-500 hover:shadow-xl overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/30 to-secondary-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Main Content */}
      <div className="relative z-10 p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h3 className="font-display font-bold text-slate-900 text-xl mb-2 group-hover:text-primary-700 transition-colors duration-300">
              {job.job_title}
            </h3>
            <div
              dangerouslySetInnerHTML={{ __html: job.description }}
              className="prose prose-sm max-w-none text-slate-600 leading-relaxed mb-4 line-clamp-2"
            />
          </div>
          <div className="ml-4 flex-shrink-0">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 border border-primary-200 uppercase tracking-wider">
              {job.department}
            </span>
          </div>
        </div>

        {/* Job Details Meta Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-primary-500" />
            <span className="text-sm text-slate-600 capitalize">
              {job.location}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Briefcase size={14} className="text-secondary-500" />
            <span className="text-sm text-slate-600 capitalize">
              {job.job_type}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-primary-500" />
            <span className="text-sm text-slate-600">
              {job.experience || "Not Specified"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-secondary-500" />
            <span className="text-sm text-slate-600">
              Posted{" "}
              {new Date(job.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
          {/* Dynamic Apply-By Deadline Field Integration */}
          <div className="flex items-center gap-2 col-span-2 md:col-span-1">
            <Calendar size={14} className="text-red-500" />
            <span className="text-sm font-medium text-slate-600">
              {job.deadline ? (
                <>
                  Apply by{" "}
                  {new Date(job.deadline).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </>
              ) : (
                "Open Until Filled"
              )}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={toggleExpanded}
            className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-300"
          >
            <span>{isExpanded ? "Less Details" : "More Details"}</span>
            <ChevronDown
              size={16}
              className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
            />
          </button>

          <button
            onClick={handleApply}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
          >
            <Mail size={16} />
            <span>Apply Now</span>
            <ExternalLink size={14} />
          </button>
        </div>

        {/* Expanded Content Expansion Panel */}
        <div
          ref={contentRef}
          className="overflow-hidden"
          style={{ height: 0, opacity: 0 }}
        >
          <div className="pt-6 border-t border-slate-200 mt-6">
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <ChevronRight size={16} className="text-primary-500" />
                  Full Position Overview
                </h4>
                <div
                  dangerouslySetInnerHTML={{ __html: job.description }}
                  className="prose prose-sm max-w-none text-slate-600 leading-relaxed"
                />
              </div>

              {/* Secure rendering for structured legacy lists if supported by future backend updates */}
              {(job.requirements || job.responsibilities) && (
                <div className="grid md:grid-cols-2 gap-8 pt-2">
                  {job.requirements && Array.isArray(job.requirements) && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                        Requirements
                      </h4>
                      <ul className="space-y-2">
                        {job.requirements.map((req, i) => (
                          <li
                            key={i}
                            className="text-sm text-slate-600 flex items-start gap-2"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {job.responsibilities &&
                    Array.isArray(job.responsibilities) && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary-400" />
                          Responsibilities
                        </h4>
                        <ul className="space-y-2">
                          {job.responsibilities.map((resp, i) => (
                            <li
                              key={i}
                              className="text-sm text-slate-600 flex items-start gap-2"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 mt-2 flex-shrink-0" />
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Position Counter Index */}
      <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
        <span className="text-xs font-bold text-slate-600">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
};

const JobListings = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);

  // See More State Counter Configuration
  const [visibleCount, setVisibleCount] = useState(5);

  const templateConfig = careersData?.positions || {
    headline: "Open Positions",
    headlineAccent: "Board",
    description:
      "Explore opportunities to collaborate and build features inside our ecosystems.",
  };

  /* ─────────────────────────────────────────────
     FETCH DYNAMIC DATA VIA TANSTACK QUERY
  ───────────────────────────────────────────── */
  const {
    data: jobsResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["public-jobs-listings-feed"],
    queryFn: async () => {
      const response = await api.get("/api/jobs/public");
      return response.data;
    },
  });

  const allJobs = Array.isArray(jobsResponse)
    ? jobsResponse
    : jobsResponse?.jobs || [];
  const visibleJobs = allJobs.slice(0, visibleCount);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current?.children ?? [],
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.2,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 80%",
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSeeMore = () => {
    setVisibleCount((prev) => prev + 5);
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-slate-50 overflow-hidden"
    >
      {/* Background Decor Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-secondary-600/5 blur-[120px] rounded-full" />
      </div>

      <div className="container mx-auto relative z-10 px-6 max-w-6xl">
        {/* Title Block Header */}
        <div ref={headerRef} className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tighter leading-none">
            {templateConfig.headline}{" "}
            <span className="bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">
              {templateConfig.headlineAccent}
            </span>
          </h2>
          <p className="text-slate-600 text-lg md:text-xl font-light leading-relaxed">
            {templateConfig.description}
          </p>
        </div>

        {/* Loading Spinner Indicator Placeholder */}
        {isLoading && (
          <div className="w-full py-16 flex flex-col items-center justify-center space-y-3">
            <div className="w-10 h-10 border-2 border-primary-500/20 border-t-primary-500 rounded-full animate-spin" />
            <p className="text-sm font-medium text-slate-400">
              Syncing active listings...
            </p>
          </div>
        )}

        {/* Sync Failure Error State Block */}
        {isError && (
          <div className="w-full max-w-md mx-auto p-5 rounded-2xl border border-red-500/20 bg-red-500/5 flex items-center gap-3 text-red-500">
            <AlertCircle size={18} className="flex-shrink-0" />
            <p className="text-sm font-medium">
              Unable to load jobs at this time. Please check back later.
            </p>
          </div>
        )}

        {/* Render List Items Loop */}
        {!isLoading && !isError && (
          <div className="space-y-8">
            {visibleJobs.map((job, index) => (
              <JobCard key={job.id} job={job} index={index} />
            ))}
          </div>
        )}

        {/* Empty Collection Fallback State Container */}
        {!isLoading && !isError && allJobs.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-slate-200 border border-slate-300 flex items-center justify-center mx-auto mb-4">
              <Briefcase size={24} className="text-slate-500" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">
              No Open Positions
            </h3>
            <p className="text-slate-600 mb-6">
              We don't have any open positions at the moment, but we're always
              looking for talented individuals.
            </p>
            <a
              href={`mailto:${careersData?.application?.contact?.email || "hr@agora.com"}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg"
            >
              <Mail size={16} />
              Send Us Your CV
            </a>
          </div>
        )}

        {/* Dynamic Load-More Pagination Trigger Row */}
        {!isLoading && !isError && visibleCount < allJobs.length && (
          <div className="flex justify-center pt-12">
            <button
              onClick={handleSeeMore}
              className="group inline-flex items-center gap-2.5 px-8 py-4 border border-primary-500/30 text-primary-600 bg-white hover:bg-primary-50/50 hover:border-primary-500/60 font-semibold rounded-xl text-sm transition-all duration-300 transform hover:-translate-y-0.5 shadow-sm"
            >
              <span>See More Positions</span>
              <ChevronDown
                size={16}
                className="transition-transform duration-300 group-hover:translate-y-0.5"
              />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default JobListings;
